import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lock, Users, FileText, IndianRupee, Loader2, ChevronDown, ChevronUp, Plus, Save, Video, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'work_initiated', label: 'Work Initiated' },
  { value: 'under_process', label: 'Under Process' },
  { value: 'pending', label: 'Pending (Awaiting Client Info)' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_COLORS = {
  work_initiated: 'bg-blue-100 text-blue-700',
  under_process: 'bg-amber-100 text-amber-700',
  pending: 'bg-orange-100 text-orange-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

export const AdminPanel = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token') || '');
  const [loggingIn, setLoggingIn] = useState(false);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState('requests');
  const [videos, setVideos] = useState([]);
  const [newVideo, setNewVideo] = useState({ client_name: '', video_url: '', description: '' });

  const [newReq, setNewReq] = useState({
    user_name: '', user_email: '', user_phone: '', user_id: '',
    service_type: '', total_amount: '', amount_paid: '', amount_pending: '',
    estimated_days: '', status: 'work_initiated', admin_notes: ''
  });

  useEffect(() => {
    if (adminToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      verifyAndLoad();
    }
  }, []);

  const verifyAndLoad = async () => {
    setLoading(true);
    try {
      await axios.get(`${API}/admin/service-requests`);
      setAuthenticated(true);
      fetchAll();
    } catch {
      localStorage.removeItem('admin_token');
      setAdminToken('');
      setAuthenticated(false);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data.access_token;
      setAdminToken(token);
      localStorage.setItem('admin_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuthenticated(true);
      fetchAll();
    } catch {
      toast.error('Invalid admin password');
    } finally {
      setLoggingIn(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [reqRes, clientRes, videoRes] = await Promise.all([
        axios.get(`${API}/admin/service-requests`),
        axios.get(`${API}/admin/clients`),
        axios.get(`${API}/video-testimonials`)
      ]);
      setRequests(reqRes.data);
      setClients(clientRes.data);
      setVideos(videoRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await axios.put(`${API}/admin/service-requests/${id}`, updates);
      toast.success('Updated successfully');
      fetchAll();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/service-requests/create`, {
        ...newReq,
        total_amount: parseFloat(newReq.total_amount) || 0,
        amount_paid: parseFloat(newReq.amount_paid) || 0,
        amount_pending: parseFloat(newReq.amount_pending) || 0,
      });
      toast.success('Service request created');
      setShowCreate(false);
      setNewReq({ user_name: '', user_email: '', user_phone: '', user_id: '', service_type: '', total_amount: '', amount_paid: '', amount_pending: '', estimated_days: '', status: 'work_initiated', admin_notes: '' });
      fetchAll();
    } catch {
      toast.error('Failed to create request');
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/video-testimonials`, newVideo);
      toast.success('Video testimonial added');
      setNewVideo({ client_name: '', video_url: '', description: '' });
      fetchAll();
    } catch {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Delete this video testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/video-testimonials/${id}`);
      toast.success('Video deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken('');
    setAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-24" data-testid="admin-login-page">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Lock className="h-10 w-10 mx-auto mb-2 text-secondary" />
            <CardTitle className="font-playfair">Admin Panel</CardTitle>
            <p className="text-sm text-muted-foreground">Enter admin password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="password" placeholder="Admin Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" />
              <Button type="submit" className="w-full" disabled={loggingIn} data-testid="admin-login-btn">
                {loggingIn && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background py-24 md:py-32" data-testid="admin-panel-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold font-playfair" data-testid="admin-title">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Manage client services, payments, and work status</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowCreate(!showCreate)} variant="outline" size="sm" data-testid="admin-create-btn">
              <Plus className="mr-1.5 h-4 w-4" /> New Request
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="admin-logout-btn">Logout</Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{clients.length}</p><p className="text-xs text-muted-foreground">Total Clients</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{requests.length}</p><p className="text-xs text-muted-foreground">Total Requests</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{requests.filter(r => r.status !== 'completed').length}</p><p className="text-xs text-muted-foreground">Active</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{requests.filter(r => r.status === 'completed').length}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
        </div>

        {/* Create Form */}
        {showCreate && (
          <Card className="mb-8 border-2 border-secondary/30" data-testid="admin-create-form">
            <CardHeader><CardTitle className="text-lg">Create Service Request</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRequest} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Client Name</Label><Input value={newReq.user_name} onChange={e => setNewReq({...newReq, user_name: e.target.value})} required /></div>
                <div><Label>Client Email</Label><Input type="email" value={newReq.user_email} onChange={e => setNewReq({...newReq, user_email: e.target.value})} /></div>
                <div><Label>Client Phone</Label><Input value={newReq.user_phone} onChange={e => setNewReq({...newReq, user_phone: e.target.value})} /></div>
                <div><Label>Service Type</Label><Input value={newReq.service_type} onChange={e => setNewReq({...newReq, service_type: e.target.value})} required placeholder="e.g. ITR Filing - Basic" /></div>
                <div><Label>Total Amount (₹)</Label><Input type="number" value={newReq.total_amount} onChange={e => setNewReq({...newReq, total_amount: e.target.value})} /></div>
                <div><Label>Amount Paid (₹)</Label><Input type="number" value={newReq.amount_paid} onChange={e => setNewReq({...newReq, amount_paid: e.target.value})} /></div>
                <div><Label>Amount Pending (₹)</Label><Input type="number" value={newReq.amount_pending} onChange={e => setNewReq({...newReq, amount_pending: e.target.value})} /></div>
                <div><Label>Est. Timeline</Label><Input value={newReq.estimated_days} onChange={e => setNewReq({...newReq, estimated_days: e.target.value})} placeholder="e.g. 2-5 working days" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={newReq.status} onValueChange={v => setNewReq({...newReq, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3"><Label>Admin Notes</Label><Textarea value={newReq.admin_notes} onChange={e => setNewReq({...newReq, admin_notes: e.target.value})} rows={2} /></div>
                <div className="md:col-span-3"><Button type="submit" className="bg-secondary hover:bg-secondary/90"><Plus className="mr-1.5 h-4 w-4" /> Create Request</Button></div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={tab === 'requests' ? 'default' : 'outline'} size="sm" onClick={() => setTab('requests')} data-testid="tab-requests">
            <FileText className="mr-1.5 h-4 w-4" /> Service Requests ({requests.length})
          </Button>
          <Button variant={tab === 'clients' ? 'default' : 'outline'} size="sm" onClick={() => setTab('clients')} data-testid="tab-clients">
            <Users className="mr-1.5 h-4 w-4" /> Clients ({clients.length})
          </Button>
          <Button variant={tab === 'videos' ? 'default' : 'outline'} size="sm" onClick={() => setTab('videos')} data-testid="tab-videos">
            <Video className="mr-1.5 h-4 w-4" /> Video Testimonials ({videos.length})
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : tab === 'requests' ? (
          <div className="space-y-3">
            {requests.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No service requests yet</p>
            ) : requests.map((req) => (
              <RequestCard key={req.id} req={req} expanded={expandedId === req.id} onToggle={() => setExpandedId(expandedId === req.id ? null : req.id)} onUpdate={handleUpdate} />
            ))}
          </div>
        ) : tab === 'clients' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client, idx) => (
              <Card key={idx} data-testid={`client-card-${idx}`}>
                <CardContent className="p-4">
                  <p className="font-semibold">{client.name}</p>
                  <p className="text-sm text-muted-foreground">{client.email}</p>
                  <p className="text-sm text-muted-foreground">{client.phone}</p>
                  <p className="text-xs text-muted-foreground mt-1">Joined: {new Date(client.created_at).toLocaleDateString('en-IN')}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : tab === 'videos' ? (
          <div className="space-y-6">
            {/* Add Video Form */}
            <Card className="border-2 border-dashed border-secondary/30" data-testid="add-video-form">
              <CardHeader><CardTitle className="text-lg">Add Video Testimonial</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleAddVideo} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>Client Name</Label><Input value={newVideo.client_name} onChange={e => setNewVideo({...newVideo, client_name: e.target.value})} required placeholder="e.g. Nishant Bhargava" /></div>
                  <div><Label>Video URL (.mp4)</Label><Input value={newVideo.video_url} onChange={e => setNewVideo({...newVideo, video_url: e.target.value})} required placeholder="https://..." /></div>
                  <div><Label>Description (optional)</Label><Input value={newVideo.description} onChange={e => setNewVideo({...newVideo, description: e.target.value})} placeholder="e.g. ITR filing feedback" /></div>
                  <div className="md:col-span-3"><Button type="submit" className="bg-secondary hover:bg-secondary/90"><Plus className="mr-1.5 h-4 w-4" /> Add Video</Button></div>
                </form>
                <p className="text-xs text-muted-foreground mt-3">Upload your video to any hosting service (or attach in this chat) and paste the URL above.</p>
              </CardContent>
            </Card>

            {/* Existing Videos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video, idx) => (
                <Card key={video.id || idx} className="overflow-hidden" data-testid={`admin-video-${idx}`}>
                  <div className="aspect-video bg-black">
                    <video controls preload="metadata" className="w-full h-full object-contain">
                      <source src={video.video_url} type="video/mp4" />
                    </video>
                  </div>
                  <CardContent className="p-4 flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-sm">{video.client_name}</p>
                      {video.description && <p className="text-xs text-muted-foreground">{video.description}</p>}
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteVideo(video.id)} data-testid={`delete-video-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {videos.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-8">No video testimonials yet</p>}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const RequestCard = ({ req, expanded, onToggle, onUpdate }) => {
  const [form, setForm] = useState({
    status: req.status || 'work_initiated',
    amount_paid: req.amount_paid || 0,
    amount_pending: req.amount_pending || 0,
    total_amount: req.total_amount || 0,
    estimated_days: req.estimated_days || '',
    estimated_completion: req.estimated_completion || '',
    admin_notes: req.admin_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(req.id, {
      status: form.status,
      amount_paid: parseFloat(form.amount_paid) || 0,
      amount_pending: parseFloat(form.amount_pending) || 0,
      total_amount: parseFloat(form.total_amount) || 0,
      estimated_days: form.estimated_days,
      estimated_completion: form.estimated_completion,
      admin_notes: form.admin_notes,
    });
    setSaving(false);
  };

  return (
    <Card className="overflow-hidden" data-testid={`admin-request-${req.id}`}>
      <div className="p-4 cursor-pointer flex justify-between items-center" onClick={onToggle}>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold truncate">{req.service_type}</p>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[req.status] || 'bg-muted'}`}>
                {STATUS_OPTIONS.find(s => s.value === req.status)?.label || req.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{req.user_name || 'Unknown'} | {req.user_email || req.user_phone || '-'}</p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold">₹{Number(req.total_amount || 0).toLocaleString('en-IN')}</p>
            <p className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString('en-IN')}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground ml-2" /> : <ChevronDown className="h-5 w-5 text-muted-foreground ml-2" />}
      </div>

      {expanded && (
        <div className="border-t p-4 bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Total Amount (₹)</Label><Input type="number" value={form.total_amount} onChange={e => setForm({...form, total_amount: e.target.value})} /></div>
            <div><Label className="text-xs">Amount Paid (₹)</Label><Input type="number" value={form.amount_paid} onChange={e => setForm({...form, amount_paid: e.target.value})} /></div>
            <div><Label className="text-xs">Amount Pending (₹)</Label><Input type="number" value={form.amount_pending} onChange={e => setForm({...form, amount_pending: e.target.value})} /></div>
            <div><Label className="text-xs">Est. Timeline</Label><Input value={form.estimated_days} onChange={e => setForm({...form, estimated_days: e.target.value})} placeholder="e.g. 2-5 working days" /></div>
            <div><Label className="text-xs">Est. Completion Date</Label><Input type="date" value={form.estimated_completion} onChange={e => setForm({...form, estimated_completion: e.target.value})} /></div>
            <div className="md:col-span-3"><Label className="text-xs">Admin Notes (visible to client)</Label><Textarea value={form.admin_notes} onChange={e => setForm({...form, admin_notes: e.target.value})} rows={2} /></div>
            <div className="md:col-span-3">
              <Button onClick={handleSave} disabled={saving} className="bg-secondary hover:bg-secondary/90" data-testid="admin-save-btn">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AdminPanel;
